"use client";
import { D as NumberFieldInput, O as NumberFieldRoot, k as Input$1 } from "../chunks/vendor-base-ui-n0ony04mhklkmnfd.js";
import { t as useImpressionRef } from "../chunks/use-impression-ref-k081rlv0dt8gci3d.js";
import { r as useAccessibleNameWarning, t as hasAccessibleName } from "../chunks/accessible-name-h2kn8kxv0zjyg24j.js";
import { t as cn } from "../chunks/cn-dk9nmc4mhy1765ud.js";
import { forwardRef, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/components/input/input.tsx
/** Input size definitions mapping names to their Tailwind classes. */
var ATOMS_INPUT_VARIANTS = { size: {
	xs: {
		classes: "gap-1 rounded-xl px-2.5 py-2 text-body-md",
		description: "Extra small input for compact UIs"
	},
	sm: {
		classes: "h-14 gap-1 rounded-2xl px-3 text-body-md",
		description: "Small input for secondary fields"
	},
	base: {
		classes: "h-16 gap-1.5 rounded-2xl px-4 text-body-lg",
		description: "Default input size"
	},
	lg: {
		classes: "h-20 gap-2 rounded-3xl px-5 text-body-xl",
		description: "Large input for prominent fields"
	}
} };
var ATOMS_INPUT_DEFAULT_VARIANTS = { size: "base" };
var ATOMS_INPUT_STYLING = {
	dimensions: {
		xs: {
			height: 36,
			paddingX: 10,
			fontSize: 16,
			borderRadius: 12,
			width: 160
		},
		sm: {
			height: 56,
			paddingX: 12,
			fontSize: 16,
			borderRadius: 16,
			width: 200
		},
		base: {
			height: 64,
			paddingX: 16,
			fontSize: 18,
			borderRadius: 16,
			width: 280
		},
		lg: {
			height: 80,
			paddingX: 20,
			fontSize: 20,
			borderRadius: 24,
			width: 320
		}
	},
	baseTokens: {
		background: "color-secondary",
		text: "text-color-surface",
		placeholder: "text-color-muted",
		ring: "color-border"
	},
	stateTokens: {
		focus: { ring: "color-active" },
		error: { ring: "color-error" },
		disabled: {
			ring: "color-border-inactive",
			background: "color-inactive-secondary",
			text: "text-color-muted"
		}
	}
};
function inputVariants({ size = ATOMS_INPUT_DEFAULT_VARIANTS.size, error = false, parentFocusIndicator = false, focusIndicator = false } = {}) {
	return cn("border-0 bg-od-bg-primary text-od-content-primary ring ring-od-border-primary outline-none focus:outline-none", "atoms-input-placeholder disabled:cursor-default disabled:text-od-content-inactive-primary disabled:bg-od-bg-inactive-secondary disabled:ring-od-border-inactive-primary", ATOMS_INPUT_VARIANTS.size[size].classes, error && "!ring-od-border-error focus:ring-od-border-error focus:ring-2", parentFocusIndicator && (error ? "focus-within:ring-od-border-error focus-within:ring-2" : "focus-within:ring-od-border-accent-brand focus-within:ring-2"), focusIndicator && (error ? "focus:ring-od-border-error focus:ring-2" : "focus:ring-od-border-accent-brand focus:ring-2"));
}
/**
* Single-line text field built on Base UI's `Input`. Use for collecting short
* free-form values (search, email, names). Sizes are tuned to the OD type ramp
* via the `size` prop and an `error` flag toggles error-ring styling.
*/
var TextInput = forwardRef((props, ref) => {
	const { className, size = "base", error = false, analyticsName, trackImpression, ...inputProps } = props;
	const viewRef = useImpressionRef(Boolean(analyticsName && trackImpression), analyticsName, "inputViewEvent", ref);
	useAccessibleNameWarning("Input", hasAccessibleName(props));
	return /* @__PURE__ */ jsx(Input$1, {
		ref: viewRef,
		className: cn(inputVariants({
			size,
			error,
			focusIndicator: true
		}), className),
		...inputProps
	});
});
TextInput.displayName = "Input";
/**
* Numeric field built on Base UI's `NumberField`. Parses and formats with
* `Intl.NumberFormat`, so grouping separators, currency symbols, and decimals
* are handled correctly (and locale-aware), the reported value is a real number,
* and caret/keyboard behavior comes for free. No stepper buttons are rendered.
*/
var NumberInput = forwardRef((props, ref) => {
	const { size = "base", error = false, analyticsName, trackImpression, className, value, defaultValue, onValueChange, onValueCommitted, decimals, min, max, step, disabled, readOnly, name, placeholder, id, onBlur, onFocus, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-describedby": ariaDescribedby, "aria-invalid": ariaInvalid } = props;
	const currency = props.type === "currency" ? props.currency ?? "USD" : void 0;
	const format = useMemo(() => {
		if (props.type === "currency") return {
			style: "currency",
			currency,
			minimumFractionDigits: decimals ?? 2,
			maximumFractionDigits: decimals ?? 2
		};
		if (props.type === "percent") return {
			style: "percent",
			maximumFractionDigits: decimals
		};
		return { maximumFractionDigits: decimals };
	}, [
		props.type,
		currency,
		decimals
	]);
	const viewRef = useImpressionRef(Boolean(analyticsName && trackImpression), analyticsName, "inputViewEvent", ref);
	useAccessibleNameWarning("Input", Boolean(ariaLabel || ariaLabelledby || id));
	return /* @__PURE__ */ jsx(NumberFieldRoot, {
		id,
		value,
		defaultValue,
		onValueChange,
		onValueCommitted,
		min,
		max,
		step,
		format,
		name,
		disabled,
		readOnly,
		children: /* @__PURE__ */ jsx(NumberFieldInput, {
			ref: viewRef,
			className: cn(inputVariants({
				size,
				error,
				focusIndicator: true
			}), className),
			placeholder,
			onBlur,
			onFocus,
			"aria-label": ariaLabel,
			"aria-labelledby": ariaLabelledby,
			"aria-describedby": ariaDescribedby,
			"aria-invalid": ariaInvalid
		})
	});
});
NumberInput.displayName = "Input";
/**
* Single-line input. Renders a free-text field by default, or a locale-formatted
* numeric field when `type` is `"number"`, `"currency"`, or `"percent"`.
*
* Provide an accessible name with `aria-label` or `aria-labelledby` — a missing
* name logs a console warning in development.
*
* @example
* ```tsx
* <Input aria-label="Search products" placeholder="Search..." />
* <Input aria-label="Email" size="sm" type="email" />
* <Input type="currency" aria-label="Price" value={price} onValueChange={setPrice} />
* <Input type="percent" aria-label="Rate" value={rate} onValueChange={setRate} />
* ```
*/
var Input = forwardRef((props, ref) => {
	if (props.type === "number" || props.type === "currency" || props.type === "percent") return /* @__PURE__ */ jsx(NumberInput, {
		ref,
		...props
	});
	return /* @__PURE__ */ jsx(TextInput, {
		ref,
		...props
	});
});
Input.displayName = "Input";
//#endregion
export { ATOMS_INPUT_DEFAULT_VARIANTS, ATOMS_INPUT_STYLING, ATOMS_INPUT_VARIANTS, Input, inputVariants };

